const macMod = '⌘'
const commonMod = 'Ctrl'

export const getModifierKey = () => {
  return isMac() ? macMod : commonMod
}

export const isMac = () => {
  // Prefer the modern User-Agent Client Hints API …
  if (
    'userAgentData' in navigator &&
    (navigator as any).userAgentData?.platform
  ) {
    return (navigator as any).userAgentData.platform === 'macOS'
  }
  // … fall back to the (deprecated) navigator.platform string.
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform)
}

/**
 * A press that asks for the context menu rather than starting a gesture or a
 * selection: the secondary button, or Control with the primary on a Mac —
 * which the engine reads as a right-click too.
 */
export const isContextMenuPress = (e: {
  button: number
  ctrlKey: boolean
}): boolean => e.button !== 0 || (e.ctrlKey && isMac())

/**
 * How each modifier is written on a Mac. Ctrl is the primary shortcut modifier
 * on Windows, so it maps to Command — the same trade getModifierKey() makes.
 */
const MAC_MODIFIERS: Record<string, string> = {
  ctrl: macMod,
  control: macMod,
  cmd: macMod,
  command: macMod,
  meta: macMod,
  alt: '⌥',
  option: '⌥',
  shift: '⇧',
}

/** The order Apple prints modifiers in, whatever order they were written. */
const MAC_MODIFIER_ORDER = ['⌃', '⌥', '⇧', macMod]

const LEADING_MODIFIER = /^(ctrl|control|cmd|command|meta|alt|option|shift)\+/i

/**
 * Writes a keyboard shortcut the way the current platform writes it:
 * "Ctrl+Shift+C" is left alone on Windows and Linux, and becomes "⇧⌘C" on a
 * Mac — symbols, Apple's modifier order, no separators.
 *
 * Call sites keep spelling shortcuts the Windows way. That is the form Word's
 * own documentation uses, and it is the only one that stays readable in source
 * to someone on either platform.
 */
export const formatShortcut = (shortcut: string): string => {
  if (!isMac()) {
    return shortcut
  }

  // Modifiers come off the front one at a time rather than by splitting on
  // "+", because the key itself can BE a plus ("Ctrl+Shift++").
  const symbols: string[] = []
  let key = shortcut
  for (
    let match = LEADING_MODIFIER.exec(key);
    match;
    match = LEADING_MODIFIER.exec(key)
  ) {
    const symbol = MAC_MODIFIERS[match[1].toLowerCase()]
    if (symbol && !symbols.includes(symbol)) {
      symbols.push(symbol)
    }
    key = key.slice(match[0].length)
  }

  symbols.sort(
    (a, b) => MAC_MODIFIER_ORDER.indexOf(a) - MAC_MODIFIER_ORDER.indexOf(b),
  )

  return `${symbols.join('')}${key}`
}
